import { computed } from "vue";

import { useBrowserStore } from "@/composables/useBrowserStore";
import { useSystemTheme } from "@/composables/useSystemTheme";

/*
 * This composable tracks the current theme based on the
 * user's preference and the operating system theme.
 *
 * The returned `release()` removes the system-theme media-query listener.
 */

/*
 * Types
 */

export type ThemePreference = "dark" | "light" | "system";
export type Theme = "dark" | "light";

const parseThemePreference = (value: string): ThemePreference => {
  switch (value) {
    case "dark":
    case "light":
    case "system":
      return value;
    default:
      return "light";
  }
};

/*
 * Functions
 */

export default function useTheme() {
  const systemTheme = useSystemTheme();
  // Load & sync theme preference with the browser's local storage.
  // Deliberately un-namespaced: the theme is a device-level preference (see
  // AGENTS.md "Browser Store: Namespace Every Key by Account").
  const { syncRawState } = useBrowserStore();
  const storedThemePreference = syncRawState("theme", "light");
  const themePreference = computed<ThemePreference>({
    get: () => parseThemePreference(storedThemePreference.value),
    set: (value) => {
      storedThemePreference.value = value;
    },
  });
  // Compute the theme based on the defined preference and operating system theme.
  const theme = computed<Theme>(() =>
    themePreference.value === "system"
      ? systemTheme.value
      : themePreference.value
  );
  return {
    themePreference,
    theme,
    // Releases the persisted-preference watcher and the system-theme
    // media-query listener.
    release: () => {
      storedThemePreference.release();
      systemTheme.release();
    },
  };
}
